import { ActionIcon, Anchor, Group, Paper, Stack, Text } from "@mantine/core";
import { createLink } from "@tanstack/react-router";
import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { useTranslation } from "react-i18next";

import type { IsoWeek, WeeklyDetail, WeeklyRow, WeeklyTodo } from "@msc/shared";

import { useSaveRowNote, useUpdateProspect } from "../../hooks/useWeekly.js";
import { todosFuerZeile } from "../../lib/weekly.js";
import { ApiErrorAlert } from "../ApiErrorAlert.js";
import { feldDerZeile, NotizFeld } from "./NotizFeld.js";
import { RowMetrics } from "./RowMetrics.js";
import { RowVisitsMaintenance } from "./RowVisitsMaintenance.js";
import { TodoList } from "./TodoList.js";

/** `Anchor component={Link}` verliert die Routen-Generics — `createLink` bindet ihn typsicher (wie in wartung.tsx). */
const RouterAnchor = createLink(
  forwardRef<HTMLAnchorElement, ComponentPropsWithoutRef<"a"> & { fw?: number; underline?: "always" | "hover" | "never" }>(function RouterAnchorBase(props, ref) {
    return <Anchor ref={ref} {...props} />;
  }),
);

export function RowCard({
  weeklyId,
  row,
  todos,
  woche,
  readOnly,
  onConflict,
  metrics,
  links,
}: {
  weeklyId: string;
  row: WeeklyRow;
  todos: WeeklyTodo[];
  woche: IsoWeek;
  readOnly: boolean;
  onConflict: () => void;
  metrics: WeeklyDetail["metrics"];
  links: WeeklyDetail["links"];
}) {
  const { t } = useTranslation();
  const saveNote = useSaveRowNote(weeklyId);
  const updateProspect = useUpdateProspect(weeklyId);

  return (
    <Paper withBorder p="sm">
      <Stack gap="xs">
        <Group justify="space-between">
          {row.customerId ? (
            <RouterAnchor to="/customers/$id" params={{ id: row.customerId }} fw={600}>
              {row.name}
            </RouterAnchor>
          ) : (
            <Text fw={600}>{row.name}</Text>
          )}
          {row.kind === "prospect" && row.prospectId && !readOnly && (
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={t("weekly.archiveProspect")}
              loading={updateProspect.isPending}
              onClick={() => updateProspect.mutate({ prospectId: row.prospectId!, archived: true })}
            >
              ×
            </ActionIcon>
          )}
        </Group>
        {row.kind !== "prospect" && row.customerId && metrics && (
          <>
            <RowMetrics customerId={row.customerId} metrics={metrics} links={links} />
            <RowVisitsMaintenance
              visit={metrics.visits?.[row.customerId]}
              maintenance={metrics.maintenance?.[row.customerId]}
              link={(tab, inhalt) => (
                <RouterAnchor to="/customers/$id" params={{ id: row.customerId! }} search={{ tab }} underline="never">
                  {inhalt}
                </RouterAnchor>
              )}
            />
          </>
        )}
        {updateProspect.error && <ApiErrorAlert error={updateProspect.error} />}
        <NotizFeld
          feld={feldDerZeile(row)}
          value={row.notes}
          version={row.notesVersion}
          readOnly={readOnly}
          onConflict={onConflict}
          save={(v) => saveNote.mutateAsync({ customerId: row.customerId, prospectId: row.prospectId, ...v })}
        />
        <TodoList
          weeklyId={weeklyId}
          todos={todosFuerZeile(todos, row)}
          woche={woche}
          readOnly={readOnly}
          customerId={row.customerId}
          prospectId={row.prospectId}
        />
      </Stack>
    </Paper>
  );
}
